import React, { ReactNode } from 'react';

import ChevronRight from '@/assets/icons/heroIcons/Chevron-right.svg';
import { Link } from '@/components/links';
import { mergeClasses } from '@/utils/style';
import { UrlObject } from 'url';
import IconHome from '@/assets/icons/heroIcons/Home.svg';
import { BreadcrumbJsonLd } from 'next-seo';

export type BreadcrumbItem = {
	title: string;
	to?: UrlObject | string;
};

interface BreadcrumbsProps {
	variant?: 'default' | 'inverted' | 'default-with-home' | 'inverted-with-home';
	items: BreadcrumbItem[];
	className?: string;
}

export function Breadcrumbs({ items, variant, className }: BreadcrumbsProps): ReactNode {
	const isInverted = variant === 'inverted' || variant === 'inverted-with-home';

	const itemClassName = mergeClasses(isInverted ? 'text-white opacity-60' : 'text-light-gray-500');

	const hasHomeIcon = variant === 'default-with-home' || variant === 'inverted-with-home';

	const breadcrumbsStructuredData = items.map((item, index) => ({
		position: index + 1,
		name: item.title,
		item: item.to,
	}));

	return (
		<div
			className={mergeClasses(
				'no-scrollbar flex flex-row flex-nowrap items-center gap-2 overflow-x-auto font-semibold',
				className
			)}
		>
			{items.map((item, index) => (
				<React.Fragment key={index}>
					{index === items.length - 1 ? (
						<span
							className={mergeClasses(
								'flex shrink-0 gap-4 whitespace-nowrap',
								isInverted ? 'text-white' : 'text-base'
							)}
						>
							{hasHomeIcon && index === 0 && <IconHome className="text-light-gray-500" />}
							{item.title}
						</span>
					) : (
						<>
							{item.to ? (
								<Link
									to={item.to}
									testId="breadcrumbs"
									analyticsParam="breadcrumbs"
									className={mergeClasses(
										itemClassName,
										'flex shrink-0 gap-4 whitespace-nowrap no-underline hover:underline',
										isInverted ? 'hover:text-white' : 'hover:text-light-gray-500'
									)}
								>
									{hasHomeIcon && index === 0 && <IconHome className="text-light-gray-500" />}
									{item.title}
								</Link>
							) : (
								<span className={mergeClasses('shrink-0 whitespace-nowrap', itemClassName)}>
									{hasHomeIcon && index === 0 && <IconHome className="text-light-gray-500" />}

									{item.title}
								</span>
							)}
							<ChevronRight className={mergeClasses('h-5 w-5 shrink-0', itemClassName)} />
						</>
					)}
				</React.Fragment>
			))}

			<BreadcrumbJsonLd itemListElements={breadcrumbsStructuredData} />
		</div>
	);
}

export default Breadcrumbs;
